/**
 * FilterBar — layout primitive for list/table filter UIs.
 *
 * Slots:
 *   - <FilterBar.Controls>   horizontal control row (search + dropdowns + toggles)
 *   - <FilterBar.Right>      right-aligned meta + saved-view picker
 *   - <FilterBar.Chips>      active-filter chip strip (omitted when empty)
 *
 * Stays URL-state agnostic: callers wire their own state and pass `onRemove`
 * to each chip.
 */
import type { ReactNode } from 'react';
import styles from './FilterBar.module.css';

export interface FilterBarProps {
  children: ReactNode;
  className?: string;
}

export function FilterBar({ children, className }: FilterBarProps) {
  return (
    <div className={[styles.bar, className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

function Controls({ children }: { children: ReactNode }) {
  return <div className={styles.controls}>{children}</div>;
}

function Right({ children }: { children: ReactNode }) {
  return <div className={styles.right}>{children}</div>;
}

function Chips({ children }: { children: ReactNode }) {
  // Render container even when empty so layout stays stable (no jump when
  // the first chip appears). Consumers can short-circuit by not rendering
  // <FilterBar.Chips> at all.
  return <div className={styles.chips}>{children}</div>;
}

export interface FilterChipProps {
  label: ReactNode;
  onRemove: () => void;
}

export function FilterChip({ label, onRemove }: FilterChipProps) {
  return (
    <span className={styles.chip}>
      <span className={styles.chipLabel}>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className={styles.chipRemove}
        aria-label={`Remove filter ${typeof label === 'string' ? label : ''}`}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </span>
  );
}

FilterBar.Controls = Controls;
FilterBar.Right = Right;
FilterBar.Chips = Chips;
